import { useState, useMemo } from "react"
import { useMutation, useQueryClient, useQuery, useInfiniteQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { Heart, Plus } from "lucide-react"
import { 
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  ScrollArea,
  Button,
  Input,
  useRepository
} from "@city-sage/aurum-avis-labs-components"
import { QueryKeys } from "../../lib/QueryKeys"
import { Stop } from "@city-sage/core-client"
import { StopPreviewCard } from "../StopPreviewCard"

interface SavedStopsSheetProps {
  isOpen: boolean
  onClose: () => void
  itineraryId: string
  dayId: string
  nextStopIndex: number
}

export function SavedStopsSheet({ 
  isOpen, 
  onClose, 
  itineraryId, 
  dayId, 
  nextStopIndex 
}: SavedStopsSheetProps) {
  const { api } = useRepository()
  const queryClient = useQueryClient()
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null)

  // Query for saved stops (reactions with reactableType 'stop') with infinite pagination
  const {
    data: savedStopsReactionsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [QueryKeys.reaction.byReactable('stop')],
    queryFn: ({ pageParam = 1 }) => {
      return api.findAllReaction({
        filter: JSON.stringify({
          reactableType: 'stop',
        }),
        page: pageParam,
        limit: 20,
      })
    },
    getNextPageParam: (lastPage, pages) => {
      // If the last page has fewer than 20 items, we've reached the end
      if (!lastPage || lastPage.length < 20) return undefined
      return pages.length + 1 // Next page number
    },
    initialPageParam: 1,
    enabled: isOpen,
  })

  // Get unique stop IDs from all pages of reactions
  const savedStopIds = useMemo(() => {
    if (!savedStopsReactionsData?.pages) return []
    const allReactions = savedStopsReactionsData.pages.flat()
    return [...new Set(allReactions.map(reaction => reaction.reactableId))]
  }, [savedStopsReactionsData])

  // Query for actual saved stop data using the reactableIds
  const { data: savedStops } = useQuery({
    queryKey: ['saved-stops-details', savedStopIds],
    queryFn: async () => {
      if (savedStopIds.length === 0) return []
      
      try {
        // Use the new stops API endpoint that can filter by multiple IDs
        const stops = await api.findAllStop({
          filter: JSON.stringify({
            ids: savedStopIds
          })
        })
        
        return stops
      } catch (error) {
        console.error('Failed to fetch saved stops:', error)
        return []
      }
    },
    enabled: savedStopIds.length > 0 && isOpen,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Mutation to add selected stop to current itinerary
  const addStopMutation = useMutation({
    mutationFn: async (selectedStop: Stop) => {
      const stopData = await api.createStop({
        itineraryId,
        createStopDto: {
          index: nextStopIndex,
          title: selectedStop.title,
          coordinates: selectedStop.coordinates,
          dayIds: [dayId],
          address: selectedStop.address,
          startTime: selectedStop.startTime,
          durationMinutes: selectedStop.durationMinutes,
          description: selectedStop.description,
          imageUrl: selectedStop.imageUrl
        }
      })
      
      return stopData
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.day(itineraryId, dayId) })
      toast.success(`"${data.title}" added to your itinerary!`)
      onClose()
    },
    onError: (error) => {
      console.error("Failed to add saved stop:", error)
      toast.error("Failed to add stop. Please try again.")
    },
  })

  const handleStopSelect = (stopId: string) => {
    setSelectedStopId(selectedStopId === stopId ? null : stopId)
  }

  const handleAddStop = () => {
    if (!selectedStopId || !savedStops) return
    
    const selectedStop = savedStops.find((stop: Stop) => stop.id === selectedStopId)
    if (selectedStop) {
      addStopMutation.mutate(selectedStop)
    }
  }

  const handleClose = () => {
    setSelectedStopId(null)
    onClose()
  }

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="h-[90dvh] flex flex-col p-0 gap-0">
        {/* Fixed Header */}
        <DrawerHeader className="px-4 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-pink-100 flex items-center justify-center">
              <Heart className="h-4 w-4 text-pink-600" />
            </div>
            <div>
              <DrawerTitle>Saved Stops</DrawerTitle>
              <p className="text-sm text-muted-foreground">
                Add a stop from your saved collection
              </p>
            </div>
          </div>
          

        </DrawerHeader>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0">
          <ScrollArea className="h-full">
            <div className="p-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="flex gap-3 py-3">
                        <div className="w-20 h-20 bg-muted rounded-lg"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted rounded w-3/4"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                          <div className="h-3 bg-muted rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : savedStops && savedStops.length > 0 ? (
                <div className="space-y-2">
                  {savedStops.map((stop, index) => (
                    <div
                      key={stop.id}
                      className={`cursor-pointer rounded-lg border-2 transition-all ${
                        selectedStopId === stop.id
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:border-muted-foreground/20 hover:bg-muted/50'
                      }`}
                      onClick={() => handleStopSelect(stop.id)}
                    >
                      <StopPreviewCard
                        stop={stop}
                        stopNumber={index + 1}
                        itineraryId="" // Not needed for display
                        dayId="" // Not needed for display
                        isSelectable={true}
                        isSelected={selectedStopId === stop.id}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-sm">No saved stops yet.</p>
                  <p className="text-xs mt-1">Save stops by liking them to add them here.</p>
                </div>
              )}

              {/* Load More Button */}
              {hasNextPage && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="min-w-[120px]"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <div className="h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin mr-2" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Fixed Footer */}
        {selectedStopId && (
          <div className="flex-shrink-0 border-t border-border bg-background p-4">
            <Button
              onClick={handleAddStop}
              disabled={addStopMutation.isPending}
              className="w-full"
            >
              {addStopMutation.isPending ? (
                <>
                  <div className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Selected Stop
                </>
              )}
            </Button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  )
} 